import React, { createContext, useState, useCallback, useContext, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

// Fail loud in production if the env var is missing: a build that silently
// falls back to the dev backend would write live operator data into dev0 Mongo.
// Dev builds keep the dev-Heroku fallback so `npm start` still works locally.
//
// Both .env files set the var with a trailing /api, but every request below
// builds its own /api/... path, so the two doubled up into /api/api/products
// and 404'd every Sales Coach screen. Normalise to a bare origin here rather
// than dropping /api from ~15 call sites, so this holds whichever way the var
// is set. (announcements.context.js takes the opposite approach and appends
// without /api, which is why it works today.)
const KNOWBIE_API = (() => {
  const url = process.env.REACT_APP_KNOWBIE_API_URL;
  if (url) return url.replace(/\/+$/, "").replace(/\/api$/, "");
  if (process.env.NODE_ENV === "production") {
    throw new Error(
      "REACT_APP_KNOWBIE_API_URL is required for production builds. Otherwise Sales Coach would write to the dev backend.",
    );
  }
  return "https://knowbie-back-dev-ff38cc203ae3.herokuapp.com";
})();

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${Cookies.get("token")}` },
});

export const SalesCoachContext = createContext();

export const useSalesCoach = () => {
  const ctx = useContext(SalesCoachContext);
  if (!ctx) {
    throw new Error("useSalesCoach must be used within a SalesCoachProvider");
  }
  return ctx;
};

export const SalesCoachProvider = ({ children }) => {
  const [pendingProducts, setPendingProducts] = useState([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [pendingError, setPendingError] = useState(null);

  // Bottles being approved or rejected right now. A background refresh that
  // lands mid-request must not put the card back for a moment.
  const pendingInFlight = useRef(new Set());

  // `quiet`: the background check the page runs every 15 seconds so new
  // flags from staff appear without a reload (walkthrough, 15 Oct). No
  // spinner, and a failed check stays silent; the next one tries again.
  const fetchPending = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) {
      setIsLoadingPending(true);
      setPendingError(null);
    }
    try {
      const res = await axios.get(
        `${KNOWBIE_API}/api/products/catalog?review_status=pending`,
        authHeaders(),
      );
      const list = Array.isArray(res.data?.products) ? res.data.products : [];
      setPendingProducts(
        list.filter((p) => !pendingInFlight.current.has(p._id)),
      );
      if (quiet) setPendingError(null);
    } catch (err) {
      if (quiet) return;
      console.error("[SalesCoach] fetchPending failed:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        "Couldn't load the review queue. Try again.";
      setPendingError(message);
      toast.error(message);
    } finally {
      if (!quiet) setIsLoadingPending(false);
    }
  }, []);

  // `updates` is optional and powers the Path-D' "Edit & approve" flow: the
  // operator fixes the script and approves in one action, instead of having to
  // approve first and then hunt the bottle down in the Catalog tab.
  const approveProduct = useCallback(async (productId, updates) => {
    const previous = pendingProducts;
    pendingInFlight.current.add(productId);
    setPendingProducts((curr) => curr.filter((p) => p._id !== productId));
    try {
      const body = updates && Object.keys(updates).length ? { updates } : {};
      await axios.post(
        `${KNOWBIE_API}/api/products/${productId}/approve`,
        body,
        authHeaders(),
      );
      toast.success(updates ? "Saved and approved." : "Approved.");
      return { ok: true };
    } catch (err) {
      console.error("[SalesCoach] approveProduct failed:", err);
      setPendingProducts(previous);
      toast.error(
        err.response?.data?.message ||
          "Couldn't approve that product. Try again.",
      );
      return { ok: false, error: err };
    } finally {
      pendingInFlight.current.delete(productId);
    }
  }, [pendingProducts]);

  const rejectProduct = useCallback(async (productId) => {
    const previous = pendingProducts;
    pendingInFlight.current.add(productId);
    setPendingProducts((curr) => curr.filter((p) => p._id !== productId));
    try {
      await axios.post(
        `${KNOWBIE_API}/api/products/${productId}/reject`,
        {},
        authHeaders(),
      );
      toast.success("Rejected.");
    } catch (err) {
      console.error("[SalesCoach] rejectProduct failed:", err);
      setPendingProducts(previous);
      toast.error(
        err.response?.data?.message ||
          "Couldn't reject that product. Try again.",
      );
    } finally {
      pendingInFlight.current.delete(productId);
    }
  }, [pendingProducts]);

  // ----- Approved catalog (Increment 2) -----

  const [approvedProducts, setApprovedProducts] = useState([]);
  const [isLoadingApproved, setIsLoadingApproved] = useState(false);
  const [approvedError, setApprovedError] = useState(null);

  const fetchApproved = useCallback(async () => {
    setIsLoadingApproved(true);
    setApprovedError(null);
    try {
      const res = await axios.get(
        `${KNOWBIE_API}/api/products/catalog?review_status=approved`,
        authHeaders(),
      );
      const list = Array.isArray(res.data?.products) ? res.data.products : [];
      setApprovedProducts(list);
    } catch (err) {
      console.error("[SalesCoach] fetchApproved failed:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        "Couldn't load the catalog. Try again.";
      setApprovedError(message);
      toast.error(message);
    } finally {
      setIsLoadingApproved(false);
    }
  }, []);

  const editProduct = useCallback(async (productId, patch) => {
    const previous = approvedProducts;
    setApprovedProducts((curr) =>
      curr.map((p) => (p._id === productId ? { ...p, ...patch } : p)),
    );
    try {
      const res = await axios.patch(
        `${KNOWBIE_API}/api/products/${productId}`,
        patch,
        authHeaders(),
      );
      const updated = res.data?.product;
      if (updated && updated._id) {
        setApprovedProducts((curr) =>
          curr.map((p) => (p._id === productId ? updated : p)),
        );
      }
      toast.success("Saved.");
      return { ok: true };
    } catch (err) {
      console.error("[SalesCoach] editProduct failed:", err);
      setApprovedProducts(previous);
      toast.error(
        err.response?.data?.message ||
          "Couldn't save those changes. Try again.",
      );
      return { ok: false, error: err };
    }
  }, [approvedProducts]);

  // Delete is permanent, so the confirm lives on the Catalog card; this just
  // removes the row optimistically and puts it back if the server refuses.
  const deleteProduct = useCallback(async (productId) => {
    const previous = approvedProducts;
    setApprovedProducts((curr) => curr.filter((p) => p._id !== productId));
    try {
      await axios.delete(
        `${KNOWBIE_API}/api/products/${productId}/catalog`,
        authHeaders(),
      );
      toast.success("Deleted.");
      return { ok: true };
    } catch (err) {
      console.error("[SalesCoach] deleteProduct failed:", err);
      setApprovedProducts(previous);
      toast.error(
        err.response?.data?.message ||
          "Couldn't delete that bottle. Try again.",
      );
      return { ok: false };
    }
  }, [approvedProducts]);

  const setProductFeatured = useCallback(async (productId, makeFeatured, opts = {}) => {
    const previous = approvedProducts;
    setApprovedProducts((curr) =>
      curr.map((p) =>
        p._id === productId ? { ...p, featured: !!makeFeatured } : p,
      ),
    );
    try {
      // Backend has a single POST /:id/feature endpoint that reads
      // { featured, featured_until, featured_message } from the body.
      // There is no separate /unfeature route.
      const res = await axios.post(
        `${KNOWBIE_API}/api/products/${productId}/feature`,
        {
          featured: !!makeFeatured,
          featured_until: opts.featured_until || null,
          // The calendar day the manager picked, shown to everyone as is.
          featured_until_day: opts.featured_until_day || "",
          featured_message: opts.featured_message || "",
          // Explicit opt-in: backend publishes a team announcement (push +
          // app + Announcement Center) only when this is literally true.
          announce: opts.announce === true,
          // Same [{type, id, name}] shape as the Announcement Center; empty
          // means everyone in the operator's teams.
          announce_groups: Array.isArray(opts.announce_groups)
            ? opts.announce_groups
            : [],
        },
        authHeaders(),
      );
      const updated = res.data?.product;
      if (updated && updated._id) {
        setApprovedProducts((curr) =>
          curr.map((p) => (p._id === productId ? updated : p)),
        );
      }
      const announcement = res.data?.announcement;
      if (makeFeatured && announcement?.reason === "no_staff") {
        // The announcement rail targets staff through Teams; an operator with
        // no team members would otherwise "announce" into silence and assume
        // it worked (nearly happened in Gary's 1 Sep staging test).
        toast.success("Featured.");
        toast.error(
          "Nobody was notified: none of your staff are in a team yet. Add them under Teams, then re-feature.",
        );
      } else {
        toast.success(
          !makeFeatured
            ? "Unfeatured."
            : announcement?.sent === true
            ? "Featured. Your team's been notified."
            : "Featured.",
        );
      }
      return { ok: true };
    } catch (err) {
      console.error("[SalesCoach] setProductFeatured failed:", err);
      setApprovedProducts(previous);
      toast.error(
        err.response?.data?.message ||
          "Couldn't update the featured state. Try again.",
      );
      return { ok: false, error: err };
    }
  }, [approvedProducts]);

  // ----- Reports (Increment 4) -----

  const [mostScannedReport, setMostScannedReport] = useState([]);
  const [featuredEngagementReport, setFeaturedEngagementReport] = useState([]);
  const [isLoadingMostScanned, setIsLoadingMostScanned] = useState(false);
  const [isLoadingFeaturedEngagement, setIsLoadingFeaturedEngagement] = useState(false);
  const [mostScannedError, setMostScannedError] = useState(null);
  const [featuredEngagementError, setFeaturedEngagementError] = useState(null);

  // The backend has always returned which window these numbers cover and the
  // UI discarded it, so an operator saw "42 scans" with no way to know whether
  // that meant this week or since launch. Keep it and show it.
  const [mostScannedPeriod, setMostScannedPeriod] = useState("week");
  const [mostScannedSince, setMostScannedSince] = useState(null);
  // Whole-window totals ({ scans, bottles }); the rows are only the top 20.
  // Null from an older server, and the page falls back to the rows.
  const [mostScannedTotals, setMostScannedTotals] = useState(null);

  const fetchMostScanned = useCallback(async (period = "week") => {
    setIsLoadingMostScanned(true);
    setMostScannedError(null);
    try {
      const res = await axios.get(
        `${KNOWBIE_API}/api/products/reports/most-scanned?period=${encodeURIComponent(period)}`,
        authHeaders(),
      );
      // Backend returns { period, since, rows: [{ product, scan_count }] }
      const list = Array.isArray(res.data?.rows) ? res.data.rows : [];
      setMostScannedReport(list);
      // Trust the server's echo over what we asked for: if it ever clamps or
      // defaults the period, the label should say what was actually measured.
      setMostScannedPeriod(res.data?.period || period);
      setMostScannedSince(res.data?.since || null);
      setMostScannedTotals(res.data?.totals || null);
    } catch (err) {
      console.error("[SalesCoach] fetchMostScanned failed:", err);
      setMostScannedError(
        err.response?.data?.message ||
          err.message ||
          "Couldn't load this report. Try again.",
      );
    } finally {
      setIsLoadingMostScanned(false);
    }
  }, []);

  const fetchFeaturedEngagement = useCallback(async () => {
    setIsLoadingFeaturedEngagement(true);
    setFeaturedEngagementError(null);
    try {
      const res = await axios.get(
        `${KNOWBIE_API}/api/products/reports/featured-engagement`,
        authHeaders(),
      );
      // Backend returns { results: [{ product, scans_during, scans_baseline, lift_pct, meaningful }], min_scan_threshold }
      const list = Array.isArray(res.data?.results) ? res.data.results : [];
      setFeaturedEngagementReport(list);
    } catch (err) {
      console.error("[SalesCoach] fetchFeaturedEngagement failed:", err);
      setFeaturedEngagementError(
        err.response?.data?.message ||
          err.message ||
          "Couldn't load this report. Try again.",
      );
    } finally {
      setIsLoadingFeaturedEngagement(false);
    }
  }, []);

  // Team activity (replaced Featured engagement on Reports, 21 Oct): how
  // many bottles each staff member looked up, never which ones.
  const [teamActivity, setTeamActivity] = useState(null);
  const [isLoadingTeamActivity, setIsLoadingTeamActivity] = useState(false);
  const [teamActivityError, setTeamActivityError] = useState(null);

  const fetchTeamActivity = useCallback(async (period = "week") => {
    setIsLoadingTeamActivity(true);
    setTeamActivityError(null);
    try {
      const res = await axios.get(
        `${KNOWBIE_API}/api/products/reports/team-activity?period=${encodeURIComponent(period)}`,
        authHeaders(),
      );
      // { period, since, rows: [{ staff_id, first_name, last_name, scans, sent, last_scan_at }], active_staff, staff_total }
      setTeamActivity(res.data || null);
    } catch (err) {
      console.error("[SalesCoach] fetchTeamActivity failed:", err);
      setTeamActivityError(
        err.response?.data?.message ||
          err.message ||
          "Couldn't load this report. Try again.",
      );
    } finally {
      setIsLoadingTeamActivity(false);
    }
  }, []);

  // ----- Operator self-scan onboarding (Increment 3) -----

  const operatorScan = useCallback(async (file) => {
    try {
      // Backend uses multer.single("image") — must send multipart/form-data
      // with a file field named "image", NOT a JSON base64 payload.
      const formData = new FormData();
      formData.append("image", file);
      const headers = {
        ...authHeaders().headers,
        // Let the browser set Content-Type with the correct multipart boundary.
      };
      const res = await axios.post(
        `${KNOWBIE_API}/api/products/operator-scan`,
        formData,
        { headers },
      );
      const created = res.data?.product;
      const action = res.data?.action;
      if (created && created._id) {
        setApprovedProducts((curr) => {
          if (curr.some((p) => p._id === created._id)) return curr;
          return [created, ...curr];
        });
      }
      // Backend returns `action`: "created_approved" | "matched_existing"
      // | "approved_pending" | "un_rejected". Show a message that's honest
      // about whether the bottle was newly added or already existed.
      const message =
        action === "matched_existing"
          ? `"${created?.name || "That bottle"}" was already in your catalog.`
          : action === "approved_pending"
            ? `Approved "${created?.name || "that bottle"}" from your review queue.`
            : action === "un_rejected"
              ? `Restored "${created?.name || "that bottle"}" to your catalog.`
              : created?.name
                ? `Added "${created.name}" to your catalog.`
                : "Added to your catalog.";
      toast.success(message);
      return { ok: true, product: created, action };
    } catch (err) {
      console.error("[SalesCoach] operatorScan failed:", err);
      const status = err.response?.status;
      let message;
      if (status === 422) {
        message =
          err.response?.data?.ai_result?.reason ||
          "Couldn't identify that bottle. Try a clearer photo of the label.";
      } else if (status === 409) {
        message =
          err.response?.data?.message ||
          "Your catalog is at its 200-bottle cap. Reject or edit existing entries to make room.";
      } else if (status === 429) {
        // Daily scan limits (per person 40, per venue, and the total) apply
        // to the manager's own photos too. Say which, in the manager's terms;
        // the server's sentence is written for staff.
        const limit = err.response?.data?.limit;
        message =
          limit === "user"
            ? "You've reached today's limit for adding bottles by photo. Try again tomorrow."
            : limit === "venue"
              ? "Your venue has reached today's scan limit. Try again tomorrow."
              : "Adding bottles by photo is paused for today. Try again tomorrow.";
      } else if (status === 503) {
        message = "The scan service is busy. Try again in a moment.";
      } else {
        message =
          err.response?.data?.message ||
          err.message ||
          "Couldn't process that scan. Try a clearer photo.";
      }
      toast.error(message);
      return { ok: false, error: message };
    }
  }, []);

  return (
    <SalesCoachContext.Provider
      value={{
        pendingProducts,
        pendingCount: pendingProducts.length,
        isLoadingPending,
        pendingError,
        fetchPending,
        approveProduct,
        rejectProduct,
        approvedProducts,
        approvedCount: approvedProducts.length,
        featuredCount: approvedProducts.filter((p) => p.featured).length,
        isLoadingApproved,
        approvedError,
        fetchApproved,
        editProduct,
        setProductFeatured,
        deleteProduct,
        operatorScan,
        mostScannedReport,
        mostScannedPeriod,
        mostScannedSince,
        mostScannedTotals,
        featuredEngagementReport,
        isLoadingMostScanned,
        isLoadingFeaturedEngagement,
        mostScannedError,
        featuredEngagementError,
        fetchMostScanned,
        fetchFeaturedEngagement,
        teamActivity,
        isLoadingTeamActivity,
        teamActivityError,
        fetchTeamActivity,
      }}
    >
      {children}
    </SalesCoachContext.Provider>
  );
};
